import React, {
  useRef,
  useContext,
  useEffect,
  useCallback,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { captureException } from '@sentry/nextjs';
import { useBreakpoint, ErrorBoundary, useIntersection } from '@buzzfeed/react-components';
import SkipToContent from '@buzzfeed/react-components/lib/components/SkipToContent';
import { isNews } from '../../../utils/isNews';
import { isGamesPost } from '../../../utils/games';
import { ActionBar } from '../../ActionBar';
import AdUnit from '../../Ads/AdUnit';
import BuzzNewsFooter from '../../NewsFooter';
import CommentsWrapper from '../../CommentsWrapper';
import ContentBottom from '../../ContentBottom';
import CommentsContext from '../../../contexts/comments';
import DestinationContext from '../../../contexts/destination';
import FeatureImage from './components/FeatureImage';
import FooterCommunity from '../../FooterCommunity';
import Headline from '../../Headline';
import HeadlineLookbook from '../../Headline/components/HeadlineLookbook';
import LazyLoad from '../../LazyLoad';
import NewsletterSignup from '../../NewsletterSignup/lazy';
import Package from '../../Package/lazy';
import { PageOverlay } from '../../PageOverlay';
import Reactions from '../../Reactions/lazy';
import ReactionsAnimated from '../../ReactionsAnimated/lazy';
import { RelatedRecircList, BottomRecircList } from '../../RecircList/variants';
import StudiosPromoUnit from '../../StudiosPromoUnit';
import Subbuzzes from '../../Subbuzzes';
import TaboolaJP from '../../Taboola/JP';
import TaboolaAPAC from '../../Taboola/APAC';
import TaboolaIDNML from '../../Taboola/IDNML';
import TopicTags from '../../TopicTags';
import TrendingProducts from '../../TrendingProducts';
import TrendingShoppingCarousel from '../../TrendingShoppingCarousel/lazy';
import GooglePreferredSourcesButton from '../../GooglePreferredSourcesButton';
import layoutStyles from '../post.module.scss';
import ABeagleContext from '../../../contexts/abeagle';
import ShareBarBottom from '../../ShareBarBottom';
import SubbbuzzSharedModal from '../../SubbuzzSharedModal';
import {isIDNML} from '../../../utils/idnml';
import TaboolaAU from '../../Taboola/AU';


function FeaturedPost({
  buzz,
  subbuzzData,
  badges,
  showOverlay,
  setShowOverlay,
}) {
  const ref = useRef(null);
  const { isIntersecting: isFloatingButtonsEnd, setObservable: setFloatingButtonsEnd } = useIntersection({ once: true });
  const { destination } = useContext(DestinationContext);
  const { commentsEnabled } = useContext(CommentsContext);
  const isMobile = useBreakpoint('(max-width:500px)'); // page level break is based on bf_header_ui break
  const [commentCount, setCommentCount] = useState(null);
  const { experiments, getFeatureFlagValue } = useContext(ABeagleContext);
  const [taboolaAPACFF, setTaboolaAPAC] = useState(false);
  const [taboolaAUFF, setTaboolaAU] = useState(false);
  const isQuiz = buzz.isQuiz;
  const isGames = isGamesPost(buzz);
  const showTopShareBar =
    (!(buzz.shouldHideBuzzSharing || buzz.shouldShowPostContentOnly) &&
      !isMobile && !isQuiz) ||
    isNews(buzz);
  const showConfettiWithReactions = buzz.isTrivia || buzz.isPersonality;
  const showEmojiReactions = !!(buzz.published && parseInt(buzz.published, 10) > 1710959400);
  const [isShowSharedSubbuzzModal, setIsShowSharedSubbuzzModal] = useState(false);
  const sharedSubbubbuzzId = buzz?.metadata?.subbuzzId;

  const isBottomAdEligible = (buzz.isQuiz || isGames) ? isIDNML({ isBFN: isNews(buzz) }) : true;
  const designUpdateEnabled = !isNews(buzz)
    && buzz.editorial_status !== 'Advertising'
    && !buzz.isShopping
    && !buzz.isQuiz
    && !isGames
    && (buzz.country_code === 'en-us' || buzz.country_code === 'en-ca');

  // Show Shared Subbuzz Modal when subbuzzid found in buzz.metadata
  useEffect(() => {
    if (sharedSubbubbuzzId) {
      setIsShowSharedSubbuzzModal(true);
    }
  }, []);

  useEffect(() => {
    if (isNews(buzz)) {
      document.querySelector('body').style.overflowX = 'hidden';
    }
  }, [buzz, buzz.format.page_width]);

  useEffect(() => {
    if (experiments && experiments.loaded) {
      const variant = getFeatureFlagValue('taboola_apac');
      setTaboolaAPAC(variant);
      const variant_au = getFeatureFlagValue('taboola_au');
      setTaboolaAU(variant_au);
    }
  }, [experiments, getFeatureFlagValue]);

  const onOverlayClick = useCallback(() => {
    setShowOverlay(false);
  }, [setShowOverlay]);

  const renderContentBetweenTaboola = () => (
    <>
      {taboolaAPACFF && (
        <TaboolaAPAC />
      )}
      <ShareBarBottom buzz={buzz} />
      {!buzz.isShopping && (
        <>
          {!showEmojiReactions && (showConfettiWithReactions ? <ReactionsAnimated /> : <Reactions />)}
          <TaboolaIDNML />
          <CommentsWrapper
            buzz={buzz}
            commentCount={commentCount}
            onCommentsLoaded={setCommentCount}
            firstPage={100}
            repliesFirstPage={2}
            perPage={100}
            showEmojiReactions={showEmojiReactions}
            commentsEnabled={commentsEnabled}
          />
        </>
      )}
      {buzz.isShopping && (
        <TaboolaIDNML />
      )}
      {taboolaAUFF && <TaboolaAU />}
      {!isNews(buzz) && !buzz.isShopping && isBottomAdEligible && (
        <AdUnit
          type="story-bpage-desktop"
          pixiedust={{
            unit_type: 'buzz_bottom',
            unit_name: buzz.id,
            position_in_unit: 1,
          }}
          style={{ marginTop: '-2rem' }}
        />
        )
      }
      <NewsletterSignup />
      <FooterCommunity />
      {isMobile && !buzz.isShopping && isBottomAdEligible && (
        <AdUnit
          type="bigstory"
          pixiedust={{
            unit_type: 'buzz_bottom',
            unit_name: buzz.id,
            position_in_unit: 1,
          }}
        />
      )}
    </>
  );

  return (
    <main
      aria-hidden={isShowSharedSubbuzzModal}
      className={`${layoutStyles.main} ${
        layoutStyles[destination]} embed-content`}
      id="buzz-content"
      ref={ref}
    >
      <SubbbuzzSharedModal
        buzz={buzz}
        isOpen={isShowSharedSubbuzzModal}
        key={`shared-modal-${buzz.id}`}
        onRequestClose={() => setIsShowSharedSubbuzzModal(false)}
        subbuzzData={subbuzzData}
        subbuzzId={sharedSubbubbuzzId}
      />
      {buzz.longform_custom_header && (
        <FeatureImage data={buzz.longform_custom_header} />
      )}

      <div
        className={`${layoutStyles.article} ${
          layoutStyles[destination]} ${
          layoutStyles.wide
        } ${layoutStyles[buzz.bfpTheme] || ''} embed-post`}
      >
        <div className={layoutStyles.content}>
          <article className="featured-post">
            {buzz.lookbookPalette
              ? <HeadlineLookbook
                  buzz={buzz}
                  commentCount={commentCount}
                  showShareBar={showTopShareBar}
                />
              : (
                  <>
                    <Headline buzz={buzz} badges={badges} />
                    <ActionBar
                      buzz={buzz}
                      commentCount={commentCount}
                      showShareBar={showTopShareBar}
                      destination={destination}
                      isFloatingButtonsEnd={isFloatingButtonsEnd}
                    />
                  </>
                )
            }
            <GooglePreferredSourcesButton buzz={buzz} />
            <TrendingProducts buzz={buzz} postRef={ref} />
            <Subbuzzes
              buzz={buzz}
              subbuzzData={subbuzzData}
              pixiedust={{
                unit_type: 'buzz_body',
                unit_name: buzz.id,
                position_in_unit: 0,
              }}
            />
            {buzz.hasStudiosPromoUnit && <StudiosPromoUnit buzz={buzz} />}
            {isNews(buzz) && <TopicTags buzz={buzz} />}
            <BuzzNewsFooter buzz={buzz} />
            <ContentBottom buzz={buzz} />
          </article>
          <ErrorBoundary onError={captureException}>
            <AdUnit
              type="subbuzz"
              pixiedust={{
                unit_type: 'buzz_bottom',
                unit_name: buzz.id,
                position_in_unit: 0,
              }}
            />
            {buzz.classification.edition === 'Japan' && (
              <TaboolaJP isMobile={isMobile}/>
            )}
            {/* RT-1950: BFJ Taboola split feed */}
            {buzz.classification.edition === 'Japan' ? (
              <div id="taboola-split-01">
                {renderContentBetweenTaboola()}
              </div>
            ) : (
              renderContentBetweenTaboola()
            )}
          </ErrorBoundary>
        </div>
      </div>
      <ErrorBoundary onError={captureException}>
        <TrendingShoppingCarousel />
        {buzz.classification.edition === 'Japan' && (
          <LazyLoad>
            <TaboolaJP placement={2} isMobile={isMobile}/>
          </LazyLoad>
        )}
        <Package
          style={{ marginTop: isMobile ? '1rem' : '-2rem', marginBottom: '3rem' }}
          isNewDesign={true}
          isShoppingDesign={!designUpdateEnabled && !isNews(buzz)}
          variant={designUpdateEnabled ? 'refreshed' : ''}
        />
        {/*ToDo: add Ads slot*/}
        <RelatedRecircList
          listLayout={(designUpdateEnabled || buzz.isQuiz || isGames)  ? 'grid' : 'gridNarrow'}
          className={(designUpdateEnabled || buzz.isQuiz || isGames) ? 'refreshed' : ''}
          variant={designUpdateEnabled ? 'wavesSection' : 'bottomUnit'}
        />
        {isNews(buzz) && <SkipToContent jumpTo="news-footer" className="skipToFooter" label="Skip to footer" />}
        {/*ToDo: add Ads slot*/}
        <BottomRecircList className={designUpdateEnabled || buzz.isQuiz || isGames ? 'refreshed' : ''} />
        <div ref={setFloatingButtonsEnd}></div>
      </ErrorBoundary>
      {showOverlay && <PageOverlay onOverlayClick={onOverlayClick} />}
    </main>
  );
}

FeaturedPost.propTypes = {
  buzz: PropTypes.object.isRequired,
  badges: PropTypes.array,
  subbuzzData: PropTypes.shape({
    assets: PropTypes.object,
    subbuzzes: PropTypes.array,
  }),
};

export default FeaturedPost;
